// src/features/pendingTransactions/pendingTransactionsSlice.ts
import { type PayloadAction, createSlice } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";

import type { PurchaseMetalFormValues } from "../../routes/_authenticated/trade/-components/Purchase/purchaseRequest";

export type TransactionType = "purchase" | "subscription" | "refund"; // should have same types as task_typ in api/types/api.ts

export interface PendingTransaction {
  id: string;
  type: TransactionType;
  timestamp: string; // timestamp when transaction was initiated
  expiresAt: number; // timestamp when polling should stop

  metadata: PurchaseMetalFormValues; // transaction-specific data
  largestId: number;
}

export interface PendingTransactionsState {
  transactions: PendingTransaction[];
  isPolling: boolean;
  transactionsCleared: boolean;
}

const initialState: PendingTransactionsState = {
  transactions: [],
  isPolling: false,
  transactionsCleared: false,
};

const PENDING_TRANSACTION_TIMEOUT_MINUTES = 5;

const pendingTransactionsSlice = createSlice({
  name: "pendingTransactions",
  initialState,
  reducers: {
    addPendingTransaction: (state, action: PayloadAction<Omit<PendingTransaction, "expiresAt">>) => {
      const now = Date.now();
      state.transactions.push({
        ...action.payload,
        expiresAt: now + PENDING_TRANSACTION_TIMEOUT_MINUTES * 60 * 1000,
      });
      state.isPolling = true;
    },
    removePendingTransaction: (state, action: PayloadAction<string>) => {
      state.transactions = state.transactions.filter((tx) => tx.id !== action.payload);
      state.isPolling = state.transactions.length > 0;
    },
    clearExpiredTransactions: (state) => {
      const now = Date.now();
      const originalLength = state.transactions.length;
      state.transactions = state.transactions.filter((tx) => tx.expiresAt > now);
      state.isPolling = state.transactions.length > 0;
      state.transactionsCleared = originalLength !== state.transactions.length; // Return true if transactions were cleared
    },
    resetTransactionsCleared: (state) => {
      state.transactionsCleared = false;
    },
  },
});

const persistConfig = {
  key: "pendingTransactions",
  storage,
  whitelist: ["transactions", "transactionsCleared"],
};

export const { addPendingTransaction, removePendingTransaction, clearExpiredTransactions, resetTransactionsCleared } =
  pendingTransactionsSlice.actions;

export default persistReducer(persistConfig, pendingTransactionsSlice.reducer);
